/**
 * Claude Auth Terminal
 *
 * Embedded mini-terminal for Claude authentication.
 * Auto-runs `claude auth login` and handles the interactive flow.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Button } from '@/components/ui/button';
import { scheduleAuthTerminalLifecycle } from './authTerminalLifecycle';
import '@xterm/xterm/css/xterm.css';

export type TerminalStage =
  | 'waiting'           // Waiting for user to type command
  | 'login-method'      // Select login method (1, 2, or 3)
  | 'theme-selection'   // Choose theme (just press Enter)
  | 'browser-open'      // Browser opened automatically (localhost)
  | 'copy-url'          // Browser didn't open, need to copy URL (container/server)
  | 'paste-code'        // Paste the auth code
  | 'login-complete'    // Login successful, press Enter
  | 'security-notes'    // Security notes screen
  | 'terminal-setup'    // Terminal setup (Shift+Enter settings)
  | 'trust-folder'      // Trust files in folder prompt
  | 'success'           // Authentication complete (Claude dashboard shown)
  | 'error';            // Something went wrong

interface ClaudeAuthTerminalProps {
  onSuccess: () => void;
  onCancel: () => void;
  onStageChange?: (stage: TerminalStage) => void;
}

export function ClaudeAuthTerminal({ onSuccess, onCancel, onStageChange }: ClaudeAuthTerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const terminalCleanupRef = useRef<(() => void) | null>(null);
  const [authComplete, setAuthComplete] = useState(false);
  const [isTerminalFocused, setIsTerminalFocused] = useState(false);
  const [_currentStage, setCurrentStage] = useState<TerminalStage>('waiting'); // _ prefix: stage tracked via ref, state triggers re-render

  // Use ref for callbacks to avoid re-creating terminal when callbacks change
  const onSuccessRef = useRef(onSuccess);
  const onCancelRef = useRef(onCancel);
  const onStageChangeRef = useRef(onStageChange);

  // Keep refs updated
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onCancelRef.current = onCancel;
    onStageChangeRef.current = onStageChange;
  }, [onSuccess, onCancel, onStageChange]);

  // Use ref for current stage to avoid re-creating callbacks
  const currentStageRef = useRef<TerminalStage>('waiting');

  // Define checkAuthStatus first since processTerminalOutput depends on it
  const checkAuthStatus = useCallback(async () => {
    console.log('[ClaudeAuthTerminal] Checking auth status...');
    try {
      const response = await fetch('/api/claude/check-auth', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      console.log('[ClaudeAuthTerminal] Auth check result:', data);
      if (data.authenticated) {
        console.log('[ClaudeAuthTerminal] Auth confirmed! Closing wizard in 1s...');
        setAuthComplete(true);
        setTimeout(() => {
          console.log('[ClaudeAuthTerminal] Calling onSuccess...');
          onSuccessRef.current();
        }, 1000);
        return true;
      } else {
        console.log('[ClaudeAuthTerminal] Not authenticated yet');
      }
    } catch (err) {
      console.error('[ClaudeAuthTerminal] Auth check failed:', err);
    }
    return false;
  }, []);

  // Detect terminal stage from output and update
  const processTerminalOutput = useCallback((text: string) => {
    let detectedStage: TerminalStage | null = null;

    // Check for various patterns in the terminal output
    // Order matters - more specific patterns first
    if (text.includes('Select login method') || text.includes('Claude account with subscription')) {
      detectedStage = 'login-method';
    } else if (text.includes('Choose the text style') || (text.includes('Dark mode') && text.includes('Light mode'))) {
      detectedStage = 'theme-selection';
    } else if (text.includes("Browser didn't open") || text.includes('Use the url below')) {
      // Container/server environment - browser can't open automatically
      detectedStage = 'copy-url';
    } else if (text.includes('Opening browser') || text.includes('browser to')) {
      // Localhost - browser opens automatically
      detectedStage = 'browser-open';
    } else if (text.includes('Enter the code') || text.includes('Paste code here') || (text.includes('paste') && text.includes('code'))) {
      detectedStage = 'paste-code';
    } else if (text.includes('Login successful') || text.includes('Logged in as')) {
      // Login complete but need to press Enter to continue
      detectedStage = 'login-complete';
    } else if (text.includes('Security notes') || text.includes('Claude can make mistakes')) {
      detectedStage = 'security-notes';
    } else if (text.includes('terminal setup') || text.includes('Shift+Enter for newlines')) {
      detectedStage = 'terminal-setup';
    } else if (text.includes('Do you trust the files') || text.includes('trust the files in this folder') || text.includes('I trust this folder') || text.includes('Is this a project you created')) {
      detectedStage = 'trust-folder';
    } else if (
      text.includes('Welcome back') ||
      text.includes('Tips for getting started') ||
      text.includes('Claude Code v') ||  // Dashboard header: "Claude Code v2.1.19"
      (text.includes('Opus') && text.includes('Claude')) ||  // "Opus 4.5 · Claude Max"
      (text.includes('Sonnet') && text.includes('Claude'))   // Alternative model
    ) {
      // Claude Code dashboard is showing - auth is truly complete
      detectedStage = 'success';
    } else if (text.includes('error') || text.includes('failed') || text.includes('Error')) {
      detectedStage = 'error';
    }

    // Update stage if changed
    if (detectedStage && detectedStage !== currentStageRef.current) {
      currentStageRef.current = detectedStage;
      setCurrentStage(detectedStage);
      onStageChangeRef.current?.(detectedStage);

      // If we reached success stage, immediately close terminal and complete
      if (detectedStage === 'success') {
        console.log('[ClaudeAuthTerminal] Success stage detected, completing auth flow...');

        // Close WebSocket immediately to prevent any more input
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
          wsRef.current = null;
        }

        // Set auth complete and call onSuccess immediately
        setAuthComplete(true);
        console.log('[ClaudeAuthTerminal] Calling onSuccess...');
        onSuccessRef.current();
      }
    }
  }, []); // No deps needed - uses refs for callbacks

  useEffect(() => {
    const terminalContainer = terminalRef.current;
    if (!terminalContainer || terminalInstance.current) return;

    const cleanupTerminal = scheduleAuthTerminalLifecycle(() => {
      const terminal = new Terminal({
        cursorBlink: true,
        fontSize: 13,
        fontFamily: 'JetBrains Mono, Menlo, Monaco, monospace',
        theme: {
          background: '#1a1b26',
          foreground: '#a9b1d6',
          cursor: '#c0caf5',
          cursorAccent: '#1a1b26',
          selectionBackground: '#33467c',
          black: '#32344a',
          red: '#f7768e',
          green: '#9ece6a',
          yellow: '#e0af68',
          blue: '#7aa2f7',
          magenta: '#ad8ee6',
          cyan: '#449dab',
          white: '#787c99',
          brightBlack: '#444b6a',
          brightRed: '#ff7a93',
          brightGreen: '#b9f27c',
          brightYellow: '#ff9e64',
          brightBlue: '#7da6ff',
          brightMagenta: '#bb9af7',
          brightCyan: '#0db9d7',
          brightWhite: '#acb0d0',
        },
        rows: 15,
        cols: 120,
      });

      const fit = new FitAddon();
      const webLinks = new WebLinksAddon();

      terminal.loadAddon(fit);
      terminal.loadAddon(webLinks);
      terminal.open(terminalContainer);

      // Initial fit - will be refined when WebSocket opens
      fit.fit();

      terminalInstance.current = terminal;
      fitAddon.current = fit;

      // Connect to WebSocket (same as terminal tray - no command param)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/pty`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        terminal.focus();
        setIsTerminalFocused(true);
        // Fit and send size to PTY now that connection is open
        fit.fit();
        ws.send(JSON.stringify({
          type: 'resize',
          cols: terminal.cols,
          rows: terminal.rows,
        }));
      };

      // Track terminal focus state
      const textarea = terminalContainer.querySelector('textarea');
      const handleFocus = () => setIsTerminalFocused(true);
      const handleBlur = () => setIsTerminalFocused(false);

      if (textarea) {
        textarea.addEventListener('focus', handleFocus);
        textarea.addEventListener('blur', handleBlur);
      }

      ws.onmessage = (event) => {
        const rawData = event.data;

        // Handle JSON messages
        try {
          const msg = JSON.parse(rawData);

          if (msg.type === 'ping') {
            // Respond with pong
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (msg.type === 'ready' || msg.type === 'exit' || msg.type === 'error') {
            // Control messages - don't display
            return;
          }

          if (msg.type === 'data' && msg.data) {
            // Terminal output data
            terminal.write(msg.data);

            // Detect stage from output (will trigger auth check on success)
            processTerminalOutput(msg.data);
            return;
          }
        } catch {
          // Not JSON - shouldn't happen with new protocol, but handle anyway
          terminal.write(rawData);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        terminal.writeln('\x1b[31mConnection error. Please try again.\x1b[0m');
      };

      ws.onclose = () => {
        // WebSocket closed - no action needed, success is handled in processTerminalOutput
        console.log('[ClaudeAuthTerminal] WebSocket closed');
      };

      // Handle terminal input - use 'data' type like Terminal.tsx
      terminal.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'data', data }));
        }
      });

      // Handle resize
      const handleResize = () => {
        if (fitAddon.current) {
          fitAddon.current.fit();
          if (ws.readyState === WebSocket.OPEN && terminalInstance.current) {
            ws.send(JSON.stringify({
              type: 'resize',
              cols: terminalInstance.current.cols,
              rows: terminalInstance.current.rows,
            }));
          }
        }
      };

      window.addEventListener('resize', handleResize);

      return {
        deactivate: () => {
          window.removeEventListener('resize', handleResize);
          if (textarea) {
            textarea.removeEventListener('focus', handleFocus);
            textarea.removeEventListener('blur', handleBlur);
          }
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
            ws.close();
          }
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
        },
        dispose: () => {
          terminal.dispose();
          if (terminalInstance.current === terminal) {
            terminalInstance.current = null;
          }
          if (fitAddon.current === fit) {
            fitAddon.current = null;
          }
        },
      };
    });

    terminalCleanupRef.current = cleanupTerminal;
    return () => {
      if (terminalCleanupRef.current === cleanupTerminal) {
        terminalCleanupRef.current = null;
      }
      cleanupTerminal();
    };
  }, [checkAuthStatus, processTerminalOutput]);

  // Clean up terminal and WebSocket when auth completes
  useEffect(() => {
    if (authComplete) {
      console.log('[ClaudeAuthTerminal] Auth complete, cleaning up terminal...');
      terminalCleanupRef.current?.();
      terminalCleanupRef.current = null;
    }
  }, [authComplete]);

  // Handle clicking on terminal to focus
  const handleTerminalClick = () => {
    if (terminalInstance.current) {
      terminalInstance.current.focus();
    }
  };

  // Don't render terminal after auth completes
  if (authComplete) {
    return (
      <div className="text-green-400 text-sm font-medium py-4">
        ✓ Authentication successful!
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Focus indicator */}
      <div className={`text-xs font-medium transition-colors ${
        isTerminalFocused ? 'text-green-400' : 'text-muted-foreground'
      }`}>
        {isTerminalFocused ? '● Terminal Active' : '○ Click terminal to activate'}
      </div>

      {/* Terminal container */}
      <div
        className={`relative overflow-hidden rounded-md border-2 w-full transition-colors ${
          isTerminalFocused
            ? 'border-green-500 ring-2 ring-green-500/20'
            : 'border-border hover:border-muted-foreground'
        }`}
        style={{ height: '400px' }}
        onClick={handleTerminalClick}
      >
        <div
          ref={terminalRef}
          className="absolute inset-0 cursor-text"
        />
      </div>

      {/* Cancel button - below terminal, left justified */}
      <Button
        variant="destructive"
        size="sm"
        onClick={onCancel}
        className="w-fit"
      >
        Cancel
      </Button>
    </div>
  );
}

export default ClaudeAuthTerminal;
